import { AskResponse, Citation } from "../../api";
import { cloneDeep } from "lodash-es";
import { ChartSpec } from "./chartTypes";
import { TableSpec } from "./tableTypes";

type DashboardLink = { url: string; label: string };

type ParsedAnswer = {
  citations: Citation[];
  markdownFormatText: string;
  intent: string;
  charts: ChartSpec[];
  tables: TableSpec[];
  // Qlik dashboard links not attached to a table (e.g. single-value answers),
  // rendered as standalone "Open in Qlik dashboard" links.
  dashboardLinks: DashboardLink[];
};

// Split a Markdown table row "| a | b |" into trimmed cells, dropping the
// empty cells produced by the leading/trailing pipes.
const splitRow = (line: string): string[] => {
  let cells = line.trim().split("|");
  if (cells.length && cells[0].trim() === "") cells = cells.slice(1);
  if (cells.length && cells[cells.length - 1].trim() === "") cells = cells.slice(0, -1);
  return cells.map((c) => c.trim());
};

const isDelimiterRow = (line: string): boolean =>
  /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)*\|?\s*$/.test(line) &&
  line.includes("-");

// Extract GitHub-flavoured Markdown pipe tables (header row + delimiter row +
// data rows) into structured TableSpec objects and strip them from the
// displayed markdown, so they can be rendered as real sortable tables
// (mirroring how chart blocks are extracted and rendered).
const extractTables = (
  text: string,
): { text: string; tables: TableSpec[]; orphanLinks: DashboardLink[] } => {
  const lines = text.split("\n");
  const tables: TableSpec[] = [];
  const kept: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const next = lines[i + 1];
    const looksLikeHeader = line.includes("|") && line.trim() !== "";
    if (looksLikeHeader && next !== undefined && isDelimiterRow(next)) {
      const headers = splitRow(line);
      const rows: string[][] = [];
      let j = i + 2;
      while (
        j < lines.length &&
        lines[j].includes("|") &&
        lines[j].trim() !== "" &&
        !isDelimiterRow(lines[j])
      ) {
        rows.push(splitRow(lines[j]));
        j++;
      }
      if (headers.length >= 2) {
        // Look ahead for an associated ```qlikdash block (the Qlik deep link
        // emitted right after the table), allowing blank lines in between.
        let dashboardUrl: string | undefined;
        let k = j;
        while (k < lines.length && lines[k].trim() === "") k++;
        if (k < lines.length && lines[k].trim() === "```qlikdash") {
          const closing = lines.indexOf("```", k + 1);
          if (closing !== -1) {
            try {
              const body = lines.slice(k + 1, closing).join("\n");
              const parsed = JSON.parse(body);
              if (parsed && typeof parsed.url === "string") {
                dashboardUrl = parsed.url;
              }
            } catch {
              // leave dashboardUrl undefined on malformed block
            }
            j = closing + 1; // consume the qlikdash block
          }
        }
        tables.push({ headers, rows, dashboardUrl });
        i = j;
        continue;
      }
    }
    kept.push(line);
    i++;
  }
  // Any qlikdash block not paired with a table (e.g. single-value answers)
  // becomes a standalone dashboard link instead of being dropped.
  const orphanLinks: DashboardLink[] = [];
  let result = kept.join("\n");
  result = result.replace(/```qlikdash\s*\n([\s\S]*?)\n```/g, (_m, body) => {
    try {
      const parsed = JSON.parse(body);
      if (parsed && typeof parsed.url === "string") {
        orphanLinks.push({
          url: parsed.url,
          label: parsed.label || "Open in Qlik dashboard",
        });
      }
    } catch {
      // drop malformed block
    }
    return "";
  });
  return { text: result, tables, orphanLinks };
};

let filteredCitations = [] as Citation[];

// Define a function to check if a citation with the same title already exists in filteredCitations
const isDuplicate = (citation: Citation) => {
  return (
    citation !== undefined &&
    citation.title &&
    filteredCitations.some((c) => c.title === citation.title)
  );
};

// Extract ```chart\n{...}\n``` blocks from the answer text, parse each JSON
// payload as a ChartSpec, and strip the block from the displayed markdown.
// Invalid JSON blocks are left untouched so the user can still see them.
const extractCharts = (
  text: string,
): { text: string; charts: ChartSpec[] } => {
  const charts: ChartSpec[] = [];
  const chartRegex = /```chart\s*\n([\s\S]*?)\n```/g;
  const newText = text.replace(chartRegex, (match, jsonBody) => {
    try {
      const parsed = JSON.parse(jsonBody) as ChartSpec;
      if (parsed && typeof parsed === "object" && parsed.type) {
        charts.push(parsed);
        return "";
      }
      return match;
    } catch {
      return match;
    }
  });
  return { text: newText, charts };
};

export function parseAnswer(answer: AskResponse): ParsedAnswer {
  let answerText = answer.answer;

  const { text: textWithoutCharts, charts } = extractCharts(answerText);
  answerText = textWithoutCharts;

  const { text: textWithoutTables, tables, orphanLinks } = extractTables(answerText);
  answerText = textWithoutTables;

  const citationLinks = answerText.match(/\[(doc\d\d?\d?)]/g);

  const lengthDocN = "[doc".length;

  filteredCitations = [] as Citation[];
  let citationReindex = 0;
  citationLinks?.forEach((link) => {
    // Replacing the links/citations with number
    let citationIndex = link.slice(lengthDocN, link.length - 1);
    let citation = cloneDeep(
      answer.citations[Number(citationIndex) - 1],
    ) as Citation;
    // if not valid citation remove the link
    if (!citation) {
      answerText = answerText.replaceAll(link, "");
      return;
    }
    // if title includes /documents/ remove it
    if (citation && citation.title && citation.title.includes("/documents/")) {
      citation.title = citation.title.replace("/documents/", "");
    }
    if (citation && citation.title && !isDuplicate(citation)) {
      answerText = answerText.replaceAll(link, ` ^${++citationReindex}^ `);
      citation.id = citationIndex; // original doc index to de-dupe
      citation.reindex_id = citationReindex.toString(); // reindex from 1 for display
      filteredCitations.push(citation);
    } else {
      // Replacing duplicate citation with original index
      let matchingCitation = filteredCitations.find(
        (ct) => citation && citation.title == ct.title,
      );
      if (matchingCitation) {
        answerText = answerText.replaceAll(
          link,
          ` ^${matchingCitation.reindex_id}^ `,
        );
      }
    }
  });

  return {
    citations: filteredCitations,
    markdownFormatText: answerText,
    intent: answer.intent,
    charts,
    tables,
    dashboardLinks: orphanLinks,
  };
}
